import type { PropsWithChildren } from 'react';
import { toast } from 'react-toastify';

import type {
  PlatformDetails,
  SocialPlatformType} from '../../../../utils/share';
import {
  formatMessageWithPlaceholders,
  generatePlatformShareLink
} from '../../../../utils/share';
import { DropdownMenuController } from '../../DropdownMenu';
import type { SocialMedium } from '../../SocialMedia';
import { SOCIAL_MEDIA_METADATA } from '../../SocialMedia';
import { CopyLinkControl } from './CopyLinkControl';

export type MessageMapping = Partial<Record<SocialPlatformType, string>> & { default: string };

type SharingDropdownMenuControllerProps = {
  url?: string;
  platforms?: SocialPlatformType[];
  title: string;
  messageTemplate: string | MessageMapping;
  appName?: string;
  feedbackType?: 'Review' | 'Suggestion';
};

const SharingDropdownMenuController = ({
  url,
  platforms = ['X', 'Discord', 'Telegram', 'Facebook', 'Reddit', 'LinkedIn', 'Email'],
  title,
  messageTemplate,
  appName,
  feedbackType,
  children,
}: PropsWithChildren<SharingDropdownMenuControllerProps>) => {
  const { default: defaultTemplate, ...customTemplates } =
    typeof messageTemplate === 'string' ? { default: messageTemplate } : messageTemplate;

  const onDiscordClick = (platformDetails: PlatformDetails) => {
    if (!platformDetails.message) {
      return undefined;
    }

    navigator.clipboard.writeText(
      formatMessageWithPlaceholders(
        platformDetails.message,
        platformDetails.url,
        platformDetails.appName,
        platformDetails.feedbackType,
      ),
    );

    toast.success('Link copied to clipboard! Paste it in any Discord chat.');

    return generatePlatformShareLink('Discord', platformDetails);
  };

  const options = platforms.map((platform) => {
    const iconKey = (platform === 'Email' ? 'MAIL' : platform).toUpperCase() as SocialMedium;

    const platformDetails = {
      url,
      title,
      message: customTemplates[platform] || defaultTemplate,
      appName,
      feedbackType,
    };

    return {
      label: platform === 'X' ? 'X (ex.Twitter)' : platform,
      icon: SOCIAL_MEDIA_METADATA[iconKey].icon,
      ...(platform === 'Discord'
        ? {
            onClick: () => onDiscordClick(platformDetails),
          }
        : {
            href: generatePlatformShareLink(platform, platformDetails),
          }),
    };
  });

  const menu = {
    title: 'Share Options',
    options: options,
    footer: <CopyLinkControl shareUrl={url} />,
  } as const;

  return <DropdownMenuController menu={menu}>{children}</DropdownMenuController>;
};

export { SharingDropdownMenuController };
